import React from 'react';

import { Accordion, Box, Button, Card, Dialog, H1, Separator, Shimmer, Text } from '@betterment/samba-react';
import { useParams } from 'react-router-dom';

import { useEmployersContributionConfigQuery } from '../hooks';

export const EmployerMatchCard: React.FC = () => {
  const { employerId = '' } = useParams<{ employerId: string }>();
  const { employers, loading, error } = useEmployersContributionConfigQuery(employerId);
  const employer = employers?.find(e => e.legacyId === Number(employerId));
  const { contributionConfig } = employer || {};
  const { ctaDescription, discretionaryWithTiersDescription, hasVesting, longDescription, shortDescription } =
    contributionConfig || {};

  if (error) return null;

  if (loading) {
    return (
      <Card css={{ border: '1px solid rgba(199, 199, 199, 1)', p: 'space.300', mt: 'space.300' }}>
        <Card.Header css={{ display: 'flex', justifyContent: 'space-between', mb: 'space.100' }}>
          <H1 variant="title200">Employer match</H1>
        </Card.Header>
        <Card.Content>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
            <Shimmer css={{ height: '16px', width: '100%' }} />
            <Shimmer css={{ height: '16px', width: '100%' }} />
          </Box>
        </Card.Content>
      </Card>
    );
  }

  return (
    contributionConfig && (
      <Card css={{ border: '1px solid rgba(199, 199, 199, 1)', p: 'space.300', mt: 'space.300' }}>
        <Card.Header css={{ display: 'flex', justifyContent: 'space-between', mb: 'space.100' }}>
          <H1 variant="title200">Employer match</H1>
        </Card.Header>
        <Card.Content>
          <Text css={{ display: 'block' }}>{ctaDescription}</Text>
          <Dialog overlay="default">
            <Dialog.Trigger data-samba-component={true} asChild={true}>
              <Button variant="textPrimary" css={{ mt: 'space.100', textStyle: 'text.100' }}>
                View details
              </Button>
            </Dialog.Trigger>
            <Dialog.Content
              css={{
                padding: 'space.400',
                width: '100%',
                maxWidth: 'container.xlarge',
                maxHeight: '80vh',
                overflow: 'auto',
              }}
            >
              <Dialog.Title>Your match details</Dialog.Title>
              <Text>{ctaDescription}</Text>
              {discretionaryWithTiersDescription && (
                <Text css={{ color: 'text.weak', display: 'block', mt: 'space.200' }} variant="text50">
                  Your employer provides a discretionary 401(k) match. Your match is at the discretion of your employer
                  and is subject to change.
                </Text>
              )}
              <Separator css={{ my: 'space.300' }} />
              <H1 css={{ pb: 'space.200', fontWeight: '400' }} variant="title200">
                {shortDescription}
              </H1>
              <Text css={{ display: 'block', pb: 'space.100', color: 'text.weak' }} variant="text50">
                {longDescription}
              </Text>
              {hasVesting && (
                <>
                  <Accordion type="single" collapsible={true}>
                    <Accordion.Item value="vesting">
                      <Accordion.Trigger>Your match vests</Accordion.Trigger>
                      <Accordion.Content css={{ mb: 'space.300' }}>
                        <Text css={{ color: 'text.weak' }} variant="text50">
                          These matched dollars will become yours over a period of time depending on your company's plan
                          rules. You can check with your employer to determine your company's specific vesting schedule.
                          All contributions that you make to the plan through payroll are owned by you completely. Only
                          employer match dollars are subject to vesting.
                        </Text>
                      </Accordion.Content>
                    </Accordion.Item>
                  </Accordion>
                </>
              )}
            </Dialog.Content>
          </Dialog>
        </Card.Content>
      </Card>
    )
  );
};
